import React, { useState } from 'react';
import {
  FiArrowRight,
  FiCheckCircle,
  FiExternalLink,
  FiMail,
} from 'react-icons/fi';
import { KAKAO_TALK_URL } from '../constants/contact';

const getInitialForm = () => ({
  name: '',
  email: '',
  phone: '',
  message: '',
  companyWebsite: '',
  formStartedAt: Date.now().toString(),
});

const channelButtonClass =
  'mt-4 inline-flex h-12 w-full items-center justify-center gap-2 border px-3 text-sm font-semibold transition';

const ContactChannels: React.FC = () => (
  <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
    <a
      href={KAKAO_TALK_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex min-h-[264px] min-w-0 flex-col border border-[#d9bd00] bg-[#fee500] p-5 text-[#3a2424] transition duration-200 hover:-translate-y-0.5 hover:border-[#aa8f00] hover:shadow-sm"
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="flex h-10 w-[68px] items-center justify-center bg-[#3a2424] text-sm font-bold tracking-[-0.04em] text-[#fee500]">
          TALK
        </div>
        <p className="text-[13px] font-semibold text-[#62452f]">
          빠른 상담
        </p>
      </div>

      <div className="mt-5 break-keep">
        <p className="text-[19px] font-bold leading-tight tracking-[-0.035em]">카카오톡 상담</p>
        <p className="mt-3 text-[13px] leading-6 text-[#4a3025]">
          내용을 확인한 후 순차적으로 답변드립니다.
        </p>
      </div>

      <div className="mt-auto border-t border-[#8b6f00]/20 pt-3">
        <p className="flex items-start gap-2 text-[11px] font-semibold leading-5 text-[#4a3025]">
          <FiCheckCircle className="mt-0.5 shrink-0 text-[15px]" />
          평일 빠른 답변 · 상담 내용 안전 관리
        </p>
      </div>

      <div className={`${channelButtonClass} border-[#3a2424] bg-[#3a2424] text-[#fee500] group-hover:bg-[#241515]`}>
        카카오톡 상담 시작
        <FiExternalLink className="text-[15px]" />
      </div>
    </a>

    <a
      href="mailto:contact@somniumlive.com"
      className="group flex min-h-[264px] min-w-0 flex-col border border-slate-200 bg-white p-5 text-slate-950 transition duration-200 hover:-translate-y-0.5 hover:border-[#005322]/45 hover:shadow-sm"
    >
      <div className="flex items-center gap-2.5">
        <div className="flex h-10 w-10 items-center justify-center border border-[#005322]/12 bg-[#005322]/[0.06] text-[#005322]">
          <FiMail className="text-[18px]" />
        </div>
        <p className="text-[15px] font-semibold tracking-[-0.03em] text-[#386f50]">이메일 문의</p>
      </div>

      <div className="mt-5 min-w-0 break-keep">
        <p className="break-all text-[17px] font-semibold leading-tight tracking-[-0.035em] text-slate-950">contact@somniumlive.com</p>
        <p className="mt-3 text-[13px] leading-6 text-slate-600">
          내용을 확인한 후 회신드립니다.
        </p>
      </div>

      <div className="mt-auto border-t border-slate-200 pt-3">
        <p className="flex items-start gap-2 text-[11px] font-medium leading-5 text-slate-500">
          <FiCheckCircle className="mt-0.5 shrink-0 text-[15px] text-[#386f50]" />
          기록 보관 · 자료 전달과 상세 문의에 적합
        </p>
      </div>

      <div className={`${channelButtonClass} border-[#386f50] bg-white text-[#386f50] group-hover:bg-[#386f50] group-hover:text-white`}>
        메일 보내기
        <FiExternalLink className="text-[15px]" />
      </div>
    </a>
  </div>
);

const Contact: React.FC = () => {
  const [form, setForm] = useState(getInitialForm);
  const [status, setStatus] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus(null);

    try {
      const res = await fetch('/mail.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(form),
      });

      if (!res.ok) {
        throw new Error('Request failed');
      }

      const result = await res.json();

      if (result.success) {
        setStatus('문의가 접수되었습니다.');
        setForm(getInitialForm());
      } else {
        setStatus('문의 접수에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      }
    } catch {
      setStatus('서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="scroll-mt-24 bg-slate-50 py-20">
      <div className="mx-auto max-w-[1200px] px-4">
        <div className="border border-slate-900 bg-slate-900 px-6 py-8 text-white md:px-10 md:py-10">
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#91c4a4]">Contact</p>
              <h2 className="mt-3 text-2xl font-bold leading-tight md:text-3xl">
                함께 검토할 프로젝트가 있으신가요?
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-7 text-slate-300 md:text-lg">
                아이디어 단계여도 괜찮습니다. 필요한 범위와 우선순위부터 함께 정리해 드립니다.
              </p>
            </div>
            <a
              href="#contact-form"
              className="inline-flex h-12 shrink-0 items-center justify-center gap-2 border border-white/20 bg-white px-6 text-base font-semibold text-slate-950 transition hover:bg-slate-100"
            >
              문의 남기기
              <FiArrowRight />
            </a>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[0.92fr_1.08fr]">
          <div>
            <div className="border border-slate-200 bg-white p-6 md:p-8">
              <h3 className="text-xl font-semibold text-slate-950">상담 전에 이런 내용만 있어도 충분합니다</h3>
              <div className="mt-6 space-y-3">
                {[
                  '무엇을 만들고 싶은지 또는 현재 어떤 문제가 있는지',
                  '예상 일정이나 먼저 구현하고 싶은 핵심 기능',
                  '현재 운영 중인 서비스인지 신규 구축인지 여부',
                  '관리자 화면, 앱, API, 유지보수 중 필요한 범위',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 border border-slate-200 bg-slate-50 px-4 py-4 text-sm leading-6 text-slate-600">
                    <FiCheckCircle className="mt-0.5 shrink-0 text-[#005322]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <ContactChannels />
          </div>

          <form
            id="contact-form"
            onSubmit={handleSubmit}
            className="border border-slate-200 bg-white p-6 text-slate-950 shadow-sm md:p-8"
          >
            <div className="mb-8">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#005322]">Project Inquiry</p>
              <h3 className="mt-3 text-xl font-semibold text-slate-950">프로젝트 문의</h3>
              <p className="mt-3 text-base leading-7 text-slate-600">
                간단한 설명만 남겨주셔도 됩니다. 내용을 기준으로 필요한 범위를 함께 정리하겠습니다.
              </p>
            </div>

            <div className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden">
              <label htmlFor="companyWebsite">Website</label>
              <input
                id="companyWebsite"
                type="text"
                name="companyWebsite"
                value={form.companyWebsite}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <input type="hidden" name="formStartedAt" value={form.formStartedAt} readOnly />

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">이름</span>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="담당자 이름"
                  className="h-12 w-full border border-slate-300 px-4 outline-none transition focus:border-[#005322]"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">연락처</span>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  placeholder="010-0000-0000"
                  className="h-12 w-full border border-slate-300 px-4 outline-none transition focus:border-[#005322]"
                />
              </label>
            </div>

            <label className="mt-5 block">
              <span className="mb-2 block text-sm font-semibold text-slate-700">이메일</span>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                placeholder="name@company.com"
                className="h-12 w-full border border-slate-300 px-4 outline-none transition focus:border-[#005322]"
              />
            </label>

            <label className="mt-5 block">
              <span className="mb-2 block text-sm font-semibold text-slate-700">문의 내용</span>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                placeholder="만들고 싶은 서비스, 필요한 기능, 예상 일정 등을 자유롭게 적어주세요."
                rows={6}
                className="w-full border border-slate-300 px-4 py-3 outline-none transition focus:border-[#005322]"
              />
            </label>

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 border border-[#005322] bg-[#005322] font-semibold text-white transition hover:bg-[#003a18] disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-300"
            >
              {isSubmitting ? '접수 중...' : '문의 보내기'}
              {!isSubmitting && <FiArrowRight />}
            </button>

            {status && <p className="mt-4 text-center text-sm font-semibold text-slate-600">{status}</p>}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
